import React from 'react';
import { LuSlidersHorizontal } from 'react-icons/lu';

import { Checkbox, Popover, Switch } from 'antd';
import LinkButton from 'components/common/LinkButton/LinkButton';
import { TASKS_GRID_NEW_LAYOUT_HELP } from 'constants/messageConstants';
import {
  TASKS_GRID_NEW_LAYOUT_LABEL,
  TASKS_GRID_VIEW_OPTIONS_COLUMNS_LABEL,
  TASKS_GRID_VIEW_OPTIONS_LABEL,
  TASKS_GRID_VIEW_OPTIONS_RESET_LABEL,
  TASKS_GRID_VIEW_OPTIONS_ROWS_LABEL,
  TASKS_GRID_VIEW_OPTIONS_TASK_CELL_LABEL,
  TASKS_SHEET_COLUMN_OPTION_LABELS,
  TASKS_SHEET_ROW_OPTION_LABELS,
  TASKS_SHEET_TASK_CELL_OPTION_LABELS,
} from 'constants/uiConstants';

import type { TasksViewPreferences } from './tasksViewPreferences';

// Same ghost pill as the Rotation planner's and the Analysis lens's controls.
const PILL_BUTTON_CLASS =
  'inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover';

type OptionGroupKey = 'columns' | 'taskCell' | 'rows';

const OPTION_GROUPS: {
  key: OptionGroupKey;
  title: string;
  labels: Record<string, string>;
}[] = [
  {
    key: 'columns',
    title: TASKS_GRID_VIEW_OPTIONS_COLUMNS_LABEL,
    labels: TASKS_SHEET_COLUMN_OPTION_LABELS,
  },
  {
    key: 'taskCell',
    title: TASKS_GRID_VIEW_OPTIONS_TASK_CELL_LABEL,
    labels: TASKS_SHEET_TASK_CELL_OPTION_LABELS,
  },
  {
    key: 'rows',
    title: TASKS_GRID_VIEW_OPTIONS_ROWS_LABEL,
    labels: TASKS_SHEET_ROW_OPTION_LABELS,
  },
];

interface TasksViewOptionsProps {
  preferences: TasksViewPreferences;
  onChange: (
    update: (previous: TasksViewPreferences) => TasksViewPreferences
  ) => void;
  onReset: () => void;
}

/**
 * The Tasks grid's View options (OP-2556): the new layout switch, then — with
 * it on — the sheet's columns, what the Task column shows and which rows
 * show. Task, Input name and Rate always show, so no choice leaves the grid
 * empty. Reset restores everything but the layout itself.
 */
const TasksViewOptions: React.FC<TasksViewOptionsProps> = ({
  preferences,
  onChange,
  onReset,
}) => {
  // Scrolls on its own so the panel never runs off a short screen.
  const content = (
    <div className="flex max-h-[calc(100vh-10rem)] w-64 flex-col gap-3 overflow-y-auto p-1">
      <div className="flex flex-col gap-1">
        <label className="flex cursor-pointer items-center justify-between gap-3 rounded px-2 py-1 text-sm font-semibold text-charcoal hover:bg-rowHover">
          {TASKS_GRID_NEW_LAYOUT_LABEL}
          <Switch
            size="small"
            checked={preferences.sheetLayout}
            onChange={(sheetLayout) =>
              onChange((previous) => ({ ...previous, sheetLayout }))
            }
          />
        </label>
        <p className="px-2 text-sm text-gray-dark">
          {TASKS_GRID_NEW_LAYOUT_HELP}
        </p>
      </div>

      {preferences.sheetLayout && (
        <>
          {OPTION_GROUPS.map(({ key, title, labels }) => (
            <div
              key={key}
              className="flex flex-col gap-0.5 border-t border-gray-bright pt-2"
            >
              <p className="mb-1 px-2 text-sm font-semibold text-gray-dark">
                {title}
              </p>
              {Object.entries(labels).map(([option, label]) => {
                const group: Record<string, boolean> = preferences[key];
                return (
                  <label
                    key={option}
                    className="flex cursor-pointer items-center rounded px-2 py-1 hover:bg-rowHover"
                  >
                    <Checkbox
                      checked={group[option]}
                      onChange={(event) =>
                        onChange((previous) => ({
                          ...previous,
                          [key]: {
                            ...previous[key],
                            [option]: event.target.checked,
                          },
                        }))
                      }
                    >
                      {label}
                    </Checkbox>
                  </label>
                );
              })}
            </div>
          ))}
          <div className="border-t border-gray-bright px-2 pt-2">
            <LinkButton onClick={onReset}>
              {TASKS_GRID_VIEW_OPTIONS_RESET_LABEL}
            </LinkButton>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Popover trigger="click" placement="bottomRight" content={content}>
      <button type="button" className={PILL_BUTTON_CLASS}>
        <LuSlidersHorizontal size={14} aria-hidden />
        {TASKS_GRID_VIEW_OPTIONS_LABEL}
      </button>
    </Popover>
  );
};

export default TasksViewOptions;
